import { format } from 'date-fns';
import { BadgeCategory, BadgeSource, type ResolvedUserBadge } from '../../models/Badge';
import { Gender } from '../../models/User';
import {
    formatProgressionAmount,
    getNextProgressionBadge,
    isProgressionBadge,
    resolveBadgeTitle,
} from '../../utils/badgeCatalog';

/**
 * Shared formatters for the per-badge metadata that the desktop tooltip and
 * the mobile info modal both render. Keeping these pure makes it cheap to
 * test the copy and avoids duplicating logic between the two surfaces.
 */

/** "Trenutni broj: 7" — only for progression badges with a known count. */
export function currentCountLine(badge: ResolvedUserBadge): string | null {
    if (!isProgressionBadge(badge.definition) || badge.count == null) {
        return null;
    }

    return `Trenutni broj: ${badge.count}`;
}

/** Awarded-by / awarded-on line. Always returns a string. */
export function awardedLine(badge: ResolvedUserBadge): string {
    const dateText = format(badge.awardedAt, 'd. M. yyyy.');

    if (badge.source === BadgeSource.Manual && badge.awardedByUsername) {
        return `Dodijelio: ${badge.awardedByUsername} (${dateText})`;
    }

    if (badge.source === BadgeSource.Manual) {
        return `Posebna nagrada (${dateText})`;
    }

    return `Stečeno ${dateText}`;
}

/**
 * "Sljedeća razina: Vještac (još 3 igre)" — drives engagement by showing
 * how close the user is to the next tier. Returns null at the top of a
 * track, for specials, or when the count has already crossed the next
 * threshold (stale data — let the next recompute catch up).
 *
 * `gender` picks the masculine or feminine form of the next badge's title
 * (e.g. "Vještac" vs "Vještica"). Defaults to masculine to match the
 * project-wide default for unset preferences.
 *
 * `currentStreak` only applies to streak-category badges and overrides the
 * remaining-amount calculation: the next streak tier can only be unlocked by
 * the live run, not by the historical best stored on `badge.count`. For all
 * other categories `badge.count` already reflects the live activity total, so
 * the parameter is ignored.
 */
export function nextTierLine(
    badge: ResolvedUserBadge,
    gender: Gender = Gender.Male,
    currentStreak?: number,
): string | null {
    if (!isProgressionBadge(badge.definition)) {
        return null;
    }

    const next = getNextProgressionBadge(badge.definition);
    if (!next) {
        return null;
    }

    const nextTitle = resolveBadgeTitle(next, gender);

    const effectiveCount = badge.definition.category === BadgeCategory.Streak
        ? currentStreak
        : badge.count;

    if (effectiveCount == null) {
        return `Sljedeća razina: ${nextTitle}`;
    }

    // Streak progression resets on a miss, so the remaining amount can equal
    // or exceed the next threshold even though a lower tier is already earned.
    // Floor at 1 so the copy never reads "0 more" — matches the StreakIndicator
    // tooltip — while keeping the original stale-data short-circuit for
    // non-streak tracks where count growing past a threshold means a recompute
    // is imminent.
    let remaining: number;
    if (badge.definition.category === BadgeCategory.Streak) {
        remaining = Math.max(1, next.threshold - effectiveCount);
    } else {
        remaining = next.threshold - effectiveCount;
        if (remaining <= 0) {
            return null;
        }
    }

    const amount = formatProgressionAmount(next.category, remaining);
    return amount
        ? `Sljedeća razina: ${nextTitle} (još ${amount})`
        : `Sljedeća razina: ${nextTitle}`;
}
